// How a belt part turns, and who feeds it.
//
// The answer is a property of the *network*: a neighbour feeds this part when
// it points back the way we are looking. One feeder off the axis is a corner,
// two or more is a merge — there is no single arc that serves two entries, and
// drawing one would pick a winner between two lanes that are equally real.
//
// A drawn run is fed by whatever is behind it, which may be another blueprint
// or the built lane it is extending — so the lookup takes either, and a run
// grown off the end of a working lane bends where it will bend once built.

import { type Direction, DIR_OFFSET, DIRECTIONS } from "src/engine/rules/board";
import { beltDirAt, blueprintDirAt, type IndustryState, NO_BELT } from "src/engine/rules/industry/state";

export type Bend = "straight" | "left" | "right" | "merge";

export interface BeltShape {
  bend: Bend;
  /** The directions material arrives *travelling*, in N E S W order. */
  feeders: readonly Direction[];
}

const opposite = (d: Direction): Direction => ((d + 2) % 4) as Direction;

/** A part's direction, built or drawn. `NO_BELT` off a lane. */
export const laneDirAt = (industry: IndustryState, sq: number): Direction | -1 => {
  const built = beltDirAt(industry, sq);
  return built === NO_BELT ? blueprintDirAt(industry, sq) : built;
};

export function beltShapeAt(industry: IndustryState, square: number): BeltShape | null {
  const outbound = laneDirAt(industry, square);
  if (outbound === NO_BELT) return null;

  const feeders: Direction[] = [];
  for (const direction of DIRECTIONS) {
    const neighbour = square + DIR_OFFSET[direction];
    if (laneDirAt(industry, neighbour) === opposite(direction)) feeders.push(opposite(direction));
  }
  feeders.sort((a, b) => a - b);

  if (feeders.length === 0) return { bend: "straight", feeders };
  if (feeders.length > 1) return { bend: "merge", feeders };
  return { bend: bendBetween(feeders[0], outbound), feeders };
}

/**
 * The turn between arriving one way and leaving another. Straight on, or a
 * lane doubling back on itself, is no turn: there is no sensible arc for a
 * reversal, and the arrows say what is happening.
 */
export function bendBetween(inbound: Direction, outbound: Direction): Bend {
  if (inbound === outbound || outbound === opposite(inbound)) return "straight";
  return (outbound - inbound + 4) % 4 === 1 ? "right" : "left";
}
